import { Injectable, Injector, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { defer, Observable, of, Subscription } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { TenantService } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { Broadcaster } from './broadcaster.class';
import * as _ from 'lodash';
import { first } from 'rxjs/operators';
import Pusher from 'pusher-js';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EnvironmentService } from '../environment/environment.service';
import { AuthService } from '../auth/auth.service';
import { ErrorService } from '../error/error.service';
import { DialogService } from '../dialog/dialog.service';

export const CHANNELS = {
  ALL_TENANT_USERS: {
    CHANNEL_NAME: "TENANT_USER",
    EVENTS: {
      PRINTER_ADDED: "printer_added",
      PRINTER_REMOVED: "printer_removed",
      PRINTER_UPDATE: "printer_updated",
      PRINTER_STATUS_UPDATE: "printer_status_update",
      PRINTER_CREATION_UPDATE: "printer_creation_update",
      WORKSTATION_NETWORK_CHANGED: "workstation_network_changed",
      PRINT_QUEUE_ADDED: "print_queue_added",
      PRINT_QUEUE_REMOVED: "print_queue_removed",
      PRINT_QUEUE_UPDATED: "print_queue_updated",
      PRINT_QUEUE_GROUPS_UPDATED: "print_queue_groups_updated",
      TENANT_UPDATED: "tenant_updated",
      BILLING_DATA_UPDATED: "billing_data_updated",
      AUTH_CONFIG_UPDATED: "auth_config_updated",
      BATCH_EXECUTED: 'batch_executed',
      SECURE_PRINT_CONFIGURATION_ADDED: 'secure_print_configuration_added',
      SECURE_PRINT_CONFIGURATION_UPDATED: 'secure_print_configuration_updated',
      SECURE_PRINT_CONFIGURATION_REMOVED: 'secure_print_configuration_removed',
      WORKFLOW_CREATED: 'workflow_created',
      WORKFLOW_DELETED: 'workflow_deleted',
      WORKFLOW_UPDATED: 'workflow_updated'
    }
  },
  SPECIFIC_USER: {
    CHANNEL_NAME: "USER",
    EVENTS: {
      JOB_ADDED: "job_added",
      JOB_REMOVED: "job_removed",
      JOB_STATE_CHANGED: "job_state_changed",
      JOB_UPDATED: "job_updated",
      TASK_PROGRESS_USER: "task_progress_user",
      DISCOVERY_REPORT: "discovery_report",
      USER_RELEASE_CREATED: "release_created",
      USER_RELEASE_UPDATED: "release_updated",
      USER_RELEASE_DELETED: "release_deleted",
      USER_SCAN_CREATED: "scan_created",
      USER_SCAN_UPDATED: "scan_updated",
      USER_SCAN_DELETED: "scan_deleted",
      USER_UPDATED: "user_updated"
    }
  }
};

@Injectable({
  providedIn: 'root'
})
export class EventService implements OnInit {

  private accessToken: string = null;
  private appTimer;
  private logger: Logger;
  private channels: any = _.cloneDeep(CHANNELS);
  private pusher: any;
  private pusherConfig: any = null;
  private removeSecurePrintSubscription: Subscription = null;
  public tenant = this.tenantService.tenantSignal;
  private updateSecurePrintSubscription: Subscription = null;
  public  user = this.userService.userSignal;
  private resumeEnabled = true;

  private endpointApi: string;

  public EULA_ACCEPTED_STORAGE_STRING = 'eula-accepted';
  public EULA_ACCEPTED_STORAGE_KEY = 'is-eula-accepted';

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private dialogService: DialogService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private injector: Injector,
    private loggingService: LoggingService,
    private platform: Platform,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[EventService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit(): void {
    this.startListeners();
  }

  // if the pusherConnectionState is null (meaning not configured yet), or 'disconnected' a connection should be made
  public startEventService() {
      this.logger.info('startEventService()');
      this.getAccessToken();
  }

  private startListeners(): void {
    if (!this.updateSecurePrintSubscription) {
      this.updateSecurePrintSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.SECURE_PRINT_CONFIGURATION_UPDATED) // MOVE TO MENU COMONENT
      .subscribe(data => {
        console.log('**PUSHER secure print configuration updated: eventService', data);
        this.logger.info('**PUSHER secure print configuration updated');
      });
    }

    if (!this.removeSecurePrintSubscription) {
      this.removeSecurePrintSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.SECURE_PRINT_CONFIGURATION_REMOVED) // MOVE TO MENU COMONENT
      .subscribe(data => {
        console.log('**PUSHER secure print configuration removed: eventService', data);
        this.logger.info('**PUSHER secure print configuration removed');
      });
    }

    this.broadcaster.on<string>('restartService').subscribe(() => {
      this.logger.info('service restarted: getAccessToken');
      console.log('## RESTART_SERVICE_EVENT_SERVICE getAccessToken()');
      this.getAccessToken();
    });

    this.broadcaster.on('DISABLE_RESUME').subscribe(() => {
      this.logger.info('DISABLE_RESUME');
      this.resumeEnabled = false;
    });

    this.broadcaster.on('ENABLE_RESUME').subscribe(() => {
      this.logger.info('ENABLE_RESUME');
      this.resumeEnabled = true;
    });

    this.platform.resume.subscribe(() => {
      if (this.resumeEnabled) {
        this.logger.info('platform resume');
        console.log('## PLATFORM_RESUME_EVENT_SERVICE getAccessToken()');
        if (this.hasAcceptedEula()) {
          this.getAccessToken();
        }
        this.broadcaster.broadcast('PLATFORM_RESUMED');
      }
    });
  }

  private hasAcceptedEula() {
    console.log('## HAS_ACCEPTED_EULA_EVENT_SERVICE ##');
    return this.storageService.getItemFromLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, 'deviceService - hasAcceptedEula()')
    .pipe(first())
    .subscribe((EULA_ACCEPTED) => {
      if (EULA_ACCEPTED === this.EULA_ACCEPTED_STORAGE_STRING) {
        return true;
      } else {
        return false;
      }
    });
  }

  public startTokenExpireCountDown() {
    this.logger.info('startTokenExpireCountDown()');
    // clearTimeout(this.appTimer);
    // this.appTimer = setTimeout(() => {
    //   this.logger.info('startTokenExpireCountDown() - REFRESH_FROM_COUNT_DOWN');
    //   this.broadcaster.broadcast('reload_page');
    // }, 530000); // 530000
  }

  private getAccessToken() {
    this.logger.info('getAccessToken()');

    // Use injector to ensure AuthService is available
    let authService: AuthService;
    try {
      authService = this.authService || this.injector.get(AuthService);
    } catch (error) {
      this.logger.error('Failed to get AuthService:', error);
      return;
    }

    if (!authService || typeof authService.getAccessToken !== 'function') {
      this.logger.error('AuthService or getAccessToken method is not available');
      return;
    }

    authService.getAccessToken().pipe(first())
    .subscribe({
      next: (token: string) => {
        this.accessToken = token;
        this.startTokenExpireCountDown();
        if (!this.pusherIsConnected()) {
          this.handlePusherConnection();
        }
      },
      error: (error: any) => {
        this.logger.error('getAccessToken() error:');
        this.dialogService.hideLoadingSpinnerDialog();
        // Use injector pattern to safely get ErrorService to avoid circular dependencies
        try {
          const errorService = this.injector.get(ErrorService);
          if (errorService && typeof errorService.handleHttpClientResponseError === 'function') {
            errorService.handleHttpClientResponseError(error, 'GET', '[EventService] getAccessToken()')
              .subscribe({
                next: (result) => {
                  if (result === 'unauthenticated') {
                    this.logger.info('User unauthenticated in EventService');
                  }
                },
                error: (handlingError) => {
                  this.logger.error('Error in error handling:', handlingError);
                }
              });
          } else {
            this.logger.error('ErrorService method not available');
          }
        } catch (injectionError) {
          this.logger.error('Could not inject ErrorService:', injectionError);
          // For auth errors, we can still provide some basic handling
          if (error.status === 401 || error.status === 403) {
            this.logger.info('Authentication failed in EventService - ErrorService unavailable');
          }
        }
      }
    });
  }

  private handlePusherConnection() {
    this.logger.info('handlePusherConnection()');
    this.disconnectPusher();
    this.configurePusherObject();
    this.setChannels();
  }

  private configurePusherObject() {
    this.logger.info('configurePusherObject()');
    if (this.tenant()) {
      this.logger.info('pusher_client_info_key: ' + JSON.stringify(this.tenant().embedded.PUSHER_CLIENT_INFO.key))
      this.endpointApi = this.environmentService.getEnvironmentEndpoint['endpointApi'];
      this.pusherConfig = {
        cluster: this.tenant().embedded.PUSHER_CLIENT_INFO.cluster,
        encrypted: true,
        authEndpoint: this.endpointApi + '/pusher/auth',
        auth: {
          headers: {
            Authorization: 'Bearer ' + this.accessToken
          }
        }
      };
      this.pusher = new Pusher(this.tenant().embedded.PUSHER_CLIENT_INFO.key, this.pusherConfig);
      this.pusher.logToConsole = true;
      // this.pusher.config.auth.headers.Authorization = 'Bearer ' + this.accessToken;
    } else {
      this.logger.info('configurePusherObject() - Tenant = null; no re-connect to PUSHER');
    }

  }

  private setChannels() {
    this.logger.info('setChannels()');
    if (this.tenant()) {
      _.forEach(this.channels, (channel) => {
        this.pusher.subscribe(this.tenant().embedded.PUSHER_CLIENT_INFO.channels[channel.CHANNEL_NAME]); // Check channel names
        _.forEach(channel.EVENTS, (event) => {
          this.pusher.bind('pusher:subscription_error', ((status) => {
            this.logger.info('## mysterious PUSHER subscription status: ' + JSON.stringify(status));
          }));
          this.pusher.bind(event, (data) => {
            this.logPusherData(event, data);
            this.broadcaster.broadcast(event, data);
          }, (error) => { });
          this.pusher.connection.bind("state_change", (states) => {});
        });
      });
    }
  }

  private logPusherData(event, data): void {
    this.logger.info('pusher.bind() EVENT: ' + event);
    if (event === 'printer_status_update') {
      this.logger.info('pusher.bind() printer: ' + data['printer']);
      this.logger.info('pusher.bind() pollStatus: ' + data['pollStatus']);
    }
    if (event === 'job_added') {
      this.logger.info('pusher.bind() release: ' + data['job']);
    }
    if (event === 'release_created') {
      this.logger.info('pusher.bind() release created: ' + data['release']);
    }
  }

  public resetPusher(): Observable<void> {
    this.logger.info('resetPusher()');
    this.logger.info('resetPusher() pusher: ' + !!this.pusher);
    this.logger.info('resetPusher() pusherConfig: ' + !!this.pusherConfig);
    clearTimeout(this.appTimer);
    return this.disconnectPusher();
  }

  private disconnectPusher(): Observable<void> {
    return defer(() => {
      this.logger.info('disconnectPusher() - has pusher: ' + !!this.pusher);
      if (!this.pusher) return of(void 0);
      this.pusher.unsubscribe();
      const pusherConnectionState = this.pusher.connection.state;
      if (pusherConnectionState === 'connected' || pusherConnectionState === 'connecting') {
        this.logger.info('DISCONNECTING_PUSHER');
        this.pusher.disconnect();
        this.pusher = null
      }
      this.pusherConfig = null;
      return of(void 0);
    });
  }

  public pusherIsConnected(): boolean {
    this.logger.info('pusherIsConnected()');
    if (!this.pusher) return false;
    const state = this.pusher.connection.state;
    console.log('## PUSHER state ##', state);
    return state === 'connected' || state === 'connecting';
  }
}